<template>
    <div class="h-full w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50">
        <div class="h-full container mx-auto px-4 sm:px-6 lg:px-8">
            <div class="flex flex-col h-full w-full py-4 sm:py-8">
                <!-- Breadcrumb -->
                <nav class="text-sm text-gray-600 mb-6 flex flex-wrap items-center">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <template v-if="role !== 'user'">
                        <span class="mx-1">/</span>
                        <router-link to="/survey-and-feedback-center"
                            class="hover:text-blue-700 transition font-medium">Survey & Feedback Center</router-link>
                    </template>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">My Surveys</span>
                </nav>

                <!-- Header -->
                <div class="bg-white shadow-sm rounded-lg p-6 mb-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h1 class="text-2xl font-bold text-gray-900 mb-2">📋 Available Surveys</h1>
                            <p class="text-gray-600">Participate in surveys and share your feedback</p>
                        </div>
                    </div>
                </div>

                <!-- Tabs Container -->
                <div class="bg-white rounded-2xl shadow-lg flex-auto flex flex-col overflow-hidden">
                    <!-- Tab Navigation -->
                    <div class="px-4 md:px-6 pt-6 border-b border-gray-200">
                        <div class="flex items-center space-x-4 overflow-x-auto pb-4">
                            <button @click="activeTab = 'all-surveys'"
                                class="relative whitespace-nowrap px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200"
                                :class="activeTab === 'all-surveys'
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'">
                                <font-awesome-icon icon="clipboard-list" class="mr-2" />
                                All Surveys
                            </button>
                            <button @click="activeTab = 'my-responses'"
                                class="relative whitespace-nowrap px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200"
                                :class="activeTab === 'my-responses'
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'">
                                <font-awesome-icon icon="check-circle" class="mr-2" />
                                My Submissions
                            </button>
                        </div>
                    </div>

                    <!-- Tab Content -->
                    <div class="flex-1 overflow-y-auto p-4 sm:p-6">
                        <!-- All Surveys Tab -->
                        <div v-if="activeTab === 'all-surveys'" class="h-full">
                            <!-- Loading State -->
                            <div v-if="loadingSurveys" class="flex justify-center items-center py-12">
                                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                            </div>

                            <!-- Empty State -->
                            <div v-else-if="!loadingSurveys && allSurveys.length === 0"
                                class="flex flex-col items-center justify-center py-16">
                                <div
                                    class="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mb-6">
                                    <font-awesome-icon icon="clipboard-list" class="h-12 w-12 text-blue-500" />
                                </div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Surveys Available</h2>
                                <p class="text-gray-500 text-center max-w-md">
                                    There are currently no active surveys available. Check back later for new surveys.
                                </p>
                            </div>

                            <!-- Surveys Grid -->
                            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div v-for="survey in allSurveys" :key="survey.id"
                                    class="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group cursor-pointer"
                                    @click="viewSurvey(survey)">
                                    <!-- Card Header with Gradient -->
                                    <div class="h-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>

                                    <div class="p-6 flex-1 flex flex-col">
                                        <!-- Status Badge -->
                                        <div class="flex items-center justify-between mb-4">
                                            <span v-if="survey.has_submitted"
                                                class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center gap-1">
                                                <font-awesome-icon icon="check-circle" />
                                                Submitted
                                            </span>
                                            <span v-else
                                                class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold flex items-center gap-1">
                                                <font-awesome-icon icon="clock" />
                                                Pending
                                            </span>
                                            <span
                                                :class="survey.visibility === 'public' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'"
                                                class="px-2 py-1 rounded-full text-xs font-medium">
                                                {{ survey.visibility === 'public' ? 'Public' : 'Private' }}
                                            </span>
                                        </div>

                                        <!-- Survey Title -->
                                        <h3
                                            class="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                                            {{ survey.title }}
                                        </h3>

                                        <!-- Survey Description -->
                                        <div v-if="survey.description"
                                            class="text-gray-600 text-sm mb-4 line-clamp-3 flex-1 survey-description"
                                            v-html="survey.description"></div>
                                        <p v-else class="text-gray-400 text-sm mb-4 italic flex-1">No description
                                            provided</p>

                                        <!-- Creator Info -->
                                        <div class="flex items-center gap-2 text-xs text-gray-500 mb-4">
                                            <font-awesome-icon icon="user" />
                                            <span>Created by:</span>
                                            <span v-if="survey.creator && survey.creator.token"
                                                @click.stop="goToProfile(survey.creator.token)"
                                                class="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium">
                                                {{ survey.creator.name }}
                                            </span>
                                            <span v-else class="font-medium">
                                                {{ survey.creator ? survey.creator.name : 'Unknown' }}
                                            </span>
                                        </div>

                                        <!-- Submission Info -->
                                        <div v-if="survey.has_submitted && survey.submitted_at"
                                            class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div class="flex items-center gap-2 text-xs text-green-700">
                                                <font-awesome-icon icon="calendar-check" />
                                                <span>Submitted on {{ formatDate(survey.submitted_at) }}</span>
                                            </div>
                                        </div>

                                        <!-- Action Button -->
                                        <div class="mt-auto pt-4 border-t border-gray-100">
                                            <button @click.stop="viewSurvey(survey)"
                                                :class="survey.has_submitted
                                                    ? 'w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors'
                                                    : 'w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg'">
                                                <font-awesome-icon :icon="survey.has_submitted ? 'eye' : 'edit'"
                                                    class="mr-2" />
                                                {{ survey.has_submitted ? 'View Details' : 'Take Survey' }}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Pagination -->
                            <div v-if="surveysPaginatorInfo && surveysPaginatorInfo.lastPage > 1" class="mt-6">
                                <pagination :paginatorInfo="surveysPaginatorInfoWithItems" :setPage="setSurveysPage">
                                </pagination>
                            </div>
                        </div>

                        <!-- My Submissions Tab -->
                        <div v-if="activeTab === 'my-responses'" class="h-full">
                            <!-- Loading State -->
                            <div v-if="loadingResponses" class="flex justify-center items-center py-12">
                                <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
                            </div>

                            <!-- Empty State -->
                            <div v-else-if="!loadingResponses && myResponses.length === 0"
                                class="flex flex-col items-center justify-center py-16">
                                <div
                                    class="w-24 h-24 bg-gradient-to-br from-green-100 to-blue-100 rounded-full flex items-center justify-center mb-6">
                                    <font-awesome-icon icon="check-circle" class="h-12 w-12 text-green-500" />
                                </div>
                                <h2 class="text-2xl font-semibold text-gray-800 mb-3">No Submissions Yet</h2>
                                <p class="text-gray-500 text-center max-w-md">
                                    You haven't submitted any surveys yet. Go to the "All Surveys" tab to participate in
                                    available surveys.
                                </p>
                            </div>

                            <!-- Responses Grid -->
                            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                <div v-for="response in myResponses" :key="response.id"
                                    class="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group cursor-pointer"
                                    @click="viewSurvey(response.survey)">
                                    <!-- Card Header with Gradient -->
                                    <div class="h-2 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500"></div>

                                    <div class="p-6 flex-1 flex flex-col">
                                        <!-- Status Badge -->
                                        <div class="flex items-center justify-between mb-4">
                                            <span
                                                class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold flex items-center gap-1">
                                                <font-awesome-icon icon="check-circle" />
                                                Completed
                                            </span>
                                            <span
                                                :class="response.survey.visibility === 'public' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'"
                                                class="px-2 py-1 rounded-full text-xs font-medium">
                                                {{ response.survey.visibility === 'public' ? 'Public' : 'Private' }}
                                            </span>
                                        </div>

                                        <!-- Survey Title -->
                                        <h3
                                            class="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                                            {{ response.survey.title }}
                                        </h3>

                                        <!-- Survey Description -->
                                        <div v-if="response.survey.description"
                                            class="text-gray-600 text-sm mb-4 line-clamp-3 flex-1 survey-description"
                                            v-html="response.survey.description"></div>
                                        <p v-else class="text-gray-400 text-sm mb-4 italic flex-1">No description
                                            provided</p>

                                        <!-- Creator Info -->
                                        <div class="flex items-center gap-2 text-xs text-gray-500 mb-4">
                                            <font-awesome-icon icon="user" />
                                            <span>Created by:</span>
                                            <span v-if="response.survey.creator && response.survey.creator.token"
                                                @click.stop="goToProfile(response.survey.creator.token)"
                                                class="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium">
                                                {{ response.survey.creator.name }}
                                            </span>
                                            <span v-else class="font-medium">
                                                {{ response.survey.creator ? response.survey.creator.name : 'Unknown' }}
                                            </span>
                                        </div>

                                        <!-- Submission Date -->
                                        <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                            <div class="flex items-center gap-2 text-xs text-green-700">
                                                <font-awesome-icon icon="calendar-check" />
                                                <span class="font-semibold">Submitted on:</span>
                                                <span>{{ formatDate(response.submitted_at) }}</span>
                                            </div>
                                        </div>

                                        <!-- Action Button -->
                                        <div class="mt-auto pt-4 border-t border-gray-100">
                                            <button @click.stop="viewSubmittedResponse(response)"
                                                class="w-full px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg">
                                                <font-awesome-icon icon="eye" class="mr-2" />
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Pagination -->
                            <div v-if="responsesPaginatorInfo && responsesPaginatorInfo.lastPage > 1" class="mt-6">
                                <pagination :paginatorInfo="responsesPaginatorInfoWithItems"
                                    :setPage="setResponsesPage"></pagination>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- View Submitted Response Modal -->
        <transition name="fade" mode="out-in">
            <base-full-modal v-if="showResponseModal" :title="`View Submitted Response`" @close="closeResponseModal">
                <view-submitted-response v-if="selectedResponseId && selectedSurveySlug"
                    :response-id="parseInt(selectedResponseId, 10)" :survey-slug="selectedSurveySlug">
                </view-submitted-response>
            </base-full-modal>
        </transition>
    </div>
</template>

<script>
import { gql } from '@apollo/client/core';
import Pagination from '../../components/shared/pagination.vue';
import ViewSubmittedResponse from './components/ViewSubmittedResponse.vue';

const GET_USER_SURVEYS = gql`
  query GetUserSurveys($first: Int!, $page: Int!) {
    userSurveys(first: $first, page: $page) {
      error
      error_code
      message
      data {
        id
        title
        display_title
        description
        slug
        is_active
        visibility
        thumbnail_path
        thumbnail_url
        has_submitted
        submitted_at
        created_at
        creator {
          id
          name
          token
        }
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
      }
    }
  }
`;

const GET_MY_SURVEY_RESPONSES = gql`
  query GetMySurveyResponses($first: Int!, $page: Int!) {
    mySurveyResponses(first: $first, page: $page) {
      error
      error_code
      message
      data {
        id
        submitted_at
        created_at
        survey {
          id
          title
          display_title
          description
          slug
          thumbnail_path
          thumbnail_url
          visibility
          creator {
            id
            name
            token
          }
        }
      }
      paginatorInfo {
        currentPage
        lastPage
        perPage
        total
        hasMorePages
      }
    }
  }
`;

export default {
    name: 'UserSurveys',
    components: {
        Pagination,
        ViewSubmittedResponse,
    },
    data() {
        return {
            activeTab: 'all-surveys',
            allSurveys: [],
            myResponses: [],
            loadingSurveys: false,
            loadingResponses: false,
            surveysPaginatorInfo: null,
            responsesPaginatorInfo: null,
            surveysFirst: 12,
            surveysPage: 1,
            responsesFirst: 12,
            responsesPage: 1,
            showResponseModal: false,
            selectedResponseId: null,
            selectedSurveySlug: null,
        };
    },
    computed: {
        role() {
            return this.$store.getters.role;
        },
        surveysPaginatorInfoWithItems() {
            if (!this.surveysPaginatorInfo) {
                return null;
            }
            const firstItem = this.surveysPaginatorInfo.firstItem || ((this.surveysPaginatorInfo.currentPage - 1) * this.surveysPaginatorInfo.perPage + 1);
            const lastItem = this.surveysPaginatorInfo.lastItem || Math.min(this.surveysPaginatorInfo.currentPage * this.surveysPaginatorInfo.perPage, this.surveysPaginatorInfo.total);
            return {
                ...this.surveysPaginatorInfo,
                firstItem,
                lastItem,
            };
        },
        responsesPaginatorInfoWithItems() {
            if (!this.responsesPaginatorInfo) {
                return null;
            }
            const firstItem = this.responsesPaginatorInfo.firstItem || ((this.responsesPaginatorInfo.currentPage - 1) * this.responsesPaginatorInfo.perPage + 1);
            const lastItem = this.responsesPaginatorInfo.lastItem || Math.min(this.responsesPaginatorInfo.currentPage * this.responsesPaginatorInfo.perPage, this.responsesPaginatorInfo.total);
            return {
                ...this.responsesPaginatorInfo,
                firstItem,
                lastItem,
            };
        },
    },
    watch: {
        activeTab(newTab) {
            if (newTab === 'all-surveys' && this.allSurveys.length === 0) {
                this.loadUserSurveys();
            } else if (newTab === 'my-responses' && this.myResponses.length === 0) {
                this.loadMyResponses();
            }
        },
    },
    created() {
        this.loadUserSurveys();
        // Pre-load responses so we can find response IDs when needed
        this.loadMyResponses();
    },
    methods: {
        async loadUserSurveys() {
            this.loadingSurveys = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_USER_SURVEYS,
                    variables: {
                        first: this.surveysFirst,
                        page: this.surveysPage,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.userSurveys.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: data.userSurveys.message || 'Failed to load surveys',
                    });
                } else {
                    this.allSurveys = data.userSurveys.data;
                    this.surveysPaginatorInfo = data.userSurveys.paginatorInfo;
                }
            } catch (error) {
                console.error('Error loading surveys:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to load surveys. Please try again.',
                });
            } finally {
                this.loadingSurveys = false;
            }
        },
        async loadMyResponses() {
            this.loadingResponses = true;
            try {
                const { data } = await this.$apollo.query({
                    query: GET_MY_SURVEY_RESPONSES,
                    variables: {
                        first: this.responsesFirst,
                        page: this.responsesPage,
                    },
                    fetchPolicy: 'network-only',
                });

                if (data.mySurveyResponses.error) {
                    this.$swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: data.mySurveyResponses.message || 'Failed to load your responses',
                    });
                } else {
                    this.myResponses = data.mySurveyResponses.data;
                    this.responsesPaginatorInfo = data.mySurveyResponses.paginatorInfo;
                }
            } catch (error) {
                console.error('Error loading responses:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Failed!',
                    text: 'Failed to load your responses. Please try again.',
                });
            } finally {
                this.loadingResponses = false;
            }
        },
        setSurveysPage(page) {
            this.surveysPage = page;
            this.loadUserSurveys();
        },
        setResponsesPage(page) {
            this.responsesPage = page;
            this.loadMyResponses();
        },
        async viewSurvey(survey) {
            if (survey.has_submitted) {
                // If already submitted, find the response and show modal
                // First, try to find in already loaded responses
                let response = this.myResponses.find(r => r.survey && r.survey.id === survey.id);

                // If not found and responses not loaded, load them
                if (!response && this.myResponses.length === 0) {
                    await this.loadMyResponses();
                    response = this.myResponses.find(r => r.survey && r.survey.id === survey.id);
                }

                // If still not found, try to fetch it
                if (!response) {
                    response = await this.fetchResponseForSurvey(survey.id);
                }

                if (response) {
                    this.selectedResponseId = response.id;
                    this.selectedSurveySlug = survey.slug;
                    this.showResponseModal = true;
                } else {
                    // Fallback: navigate to take page
                    this.$router.push(`/survey/take/${survey.slug}`);
                }
            } else {
                // Navigate to take survey
                this.$router.push(`/survey/take/${survey.slug}`);
            }
        },
        async fetchResponseForSurvey(surveyId) {
            try {
                const { data } = await this.$apollo.query({
                    query: GET_MY_SURVEY_RESPONSES,
                    variables: {
                        first: 100,
                        page: 1,
                    },
                    fetchPolicy: 'network-only',
                });

                if (!data.mySurveyResponses.error && data.mySurveyResponses.data) {
                    return data.mySurveyResponses.data.find(r => r.survey && r.survey.id === surveyId);
                }
            } catch (error) {
                console.error('Error fetching response:', error);
            }
            return null;
        },
        viewSubmittedResponse(response) {
            this.selectedResponseId = response.id;
            this.selectedSurveySlug = response.survey.slug;
            this.showResponseModal = true;
        },
        closeResponseModal() {
            this.showResponseModal = false;
            this.selectedResponseId = null;
            this.selectedSurveySlug = null;
        },
        goToProfile(userToken) {
            if (userToken) {
                this.$router.push(`/profile/${userToken}`);
            }
        },
        formatDate(date) {
            if (!date) return '';
            return new Date(date).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* Rich Text Description Styles */
.survey-description {
    :deep(p) {
        margin-bottom: 0.5rem;
    }

    :deep(p:last-child) {
        margin-bottom: 0;
    }

    :deep(ul),
    :deep(ol) {
        margin-left: 1.25rem;
        margin-bottom: 0.5rem;
    }

    :deep(li) {
        margin-bottom: 0.25rem;
    }

    :deep(h1),
    :deep(h2),
    :deep(h3),
    :deep(h4),
    :deep(h5),
    :deep(h6) {
        font-weight: 600;
        margin-top: 0.75rem;
        margin-bottom: 0.5rem;
        font-size: inherit;
    }

    :deep(a) {
        color: #2563eb;
        text-decoration: underline;
    }

    :deep(a:hover) {
        color: #1d4ed8;
    }

    :deep(strong) {
        font-weight: 600;
    }

    :deep(em) {
        font-style: italic;
    }

    :deep(img) {
        max-width: 100%;
        height: auto;
        border-radius: 0.375rem;
        margin: 0.5rem 0;
    }

    :deep(blockquote) {
        border-left: 3px solid #3b82f6;
        padding-left: 0.75rem;
        margin: 0.5rem 0;
        font-style: italic;
        color: #4b5563;
    }
}
</style>
